import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Card, Stack, Container, Typography, TablePagination, IconButton, Chip } from '@mui/material';
import Page from '../../components/Page';
import { ListToolbar } from '../../components/tables';
import useQuery from '../../hooks/useQuery';
import SortDropdown from '../../components/SortDropdown';
import tracker from '../../api/tracker';
import FreeSelect from '../../components/hook-form/FreeSelect';
import useNotification from '../../hooks/useNotificaction';
import AgGridWrapper, { agDefaultColDef, StatusCell } from '../../components/tables/AgGridWrapper';

const override = { margin: '0 auto' };

export default function DocRequestIndex() {
  const [docRequests, setDocRequests] = useState([]);
  const [sendNotification] = useNotification();
  const [pagination, setPagination] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [filterScope, setFilterScope] = useState('all');
  const [sort, setSort] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const filterOptions = [
    { value: 'all', label: t('all') },
    { value: 'forStatus(pending)', label: t('pending') },
    { value: 'forStatus(accepted)', label: t('accepted') },
    { value: 'forStatus(rejected)', label: t('rejected') },
  ];

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('docRequests', filter, sort, setDocRequests, sortField, setPagination, page, () => {}, filterScope);
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 500);
    return () => clearTimeout(delay);
  }, [filter, sort, page, sortField, filterScope]);

  const onStatusChange = async (status, hash) => {
    await tracker.post(`docRequests/${hash}/setStatus`, { status });
    sendNotification({ msg: t('status_updated'), variant: 'success' });
    refreshTable();
  };

  const statusOptions = [
    { value: 'accepted', label: t('accepted') },
    { value: 'pending', label: t('pending') },
    { value: 'rejected', label: t('rejected') },
  ];

  const columnDefs = [
    { field: 'created_at', headerName: t('created_at'), flex: 1, minWidth: 130, filter: 'agTextColumnFilter' },
    { field: 'requestor_name', headerName: t('requestor_name'), flex: 1, minWidth: 140, filter: 'agTextColumnFilter' },
    { field: 'phone', headerName: t('phone'), flex: 1, minWidth: 130, filter: 'agTextColumnFilter' },
    { field: 'address_line_1', headerName: t('full_address'), flex: 1, minWidth: 150, sortable: false, filter: 'agTextColumnFilter' },
    {
      field: 'status',
      headerName: t('status'),
      flex: 1,
      minWidth: 120,
      filter: 'agTextColumnFilter',
      cellRenderer: (params) => <StatusCell value={params.value} t={t} />,
    },
    {
      field: 'donation_made',
      headerName: t('donation_status'),
      width: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <Chip
          size="small"
          label={params.value ? t('donation_paid') : t('donation_unpaid')}
          sx={{
            fontWeight: 700,
            fontSize: '11px',
            color: params.value ? '#166534' : '#92400e',
            backgroundColor: params.value ? '#dcfce7' : '#fef3c7',
          }}
        />
      ),
    },
    {
      field: 'type',
      headerName: t('certificate'),
      flex: 1,
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params) => <span>{t(params.value)}</span>,
    },
    {
      headerName: t('edit_status'),
      width: 170,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <FreeSelect
          initialValue={params.data.status}
          onChange={(e) => onStatusChange(e.target.value, params.data.hashid)}
          options={statusOptions}
        />
      ),
    },
    {
      headerName: '',
      width: 70,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => (
        <IconButton size="small" color="primary" onClick={() => navigate(`/dashboard/docRequest/view/${params.data.hashid}`)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Page title={t('docRequests1')}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-start" mb={5}>
          <Typography variant="h4" gutterBottom>{t('docRequests1')}</Typography>
        </Stack>
        <Card>
          <Stack direction="row" alignItems="center" mb={1}>
            <ListToolbar numSelected={0} filterName={filter} onFilterName={(e) => setFilter(e.target.value)} placeholder={t('search_docRequests')} />
            <SortDropdown sx={{ width: '200px' }} options={filterOptions} sort={filterScope} setSort={setFilterScope} />
          </Stack>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper rowData={docRequests} columnDefs={columnDefs} defaultColDef={agDefaultColDef} />
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 10}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
        </Card>
      </Container>
    </Page>
  );
}
